import { Icon } from "@ui/components/Icon";
import { MobileSheet } from "@ui/components/MobileSheet";
import { firstSelectedKey } from "@ui/lib/selection";
import { Button } from "@ui/primitives/Button";
import { Menu, MenuItem } from "@ui/primitives/Menu";
import type { Key, Selection } from "@ui/primitives/shared";
import { actionSheet } from "./action-menu.styles";
import type { ActionMenuOption } from "./types";

export interface ActionSheetSurfaceProps {
  platform: "ios" | "android";
  /** Accessible name for the sheet + menu. */
  label: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  options: ActionMenuOption[];
  /** Optional non-interactive header above the options. */
  title?: string;
  /** iOS-only Cancel pill label. */
  cancelLabel: string;
  selectionMode?: "single";
  selectedKey?: string;
  onSelectionChange?: (key: string) => void;
}

/**
 * Mobile action sheet: a `MobileSheet` supplies travel, dismissal and focus
 * containment, and the options render as a React Aria `Menu` so keyboard
 * navigation, type-ahead, single-selection and `role="menu"` come for free.
 * Dismissal is by Cancel (iOS), swipe, scrim tap, or Escape/back.
 */
export function ActionSheetSurface({
  platform,
  label,
  isOpen,
  onOpenChange,
  options,
  title,
  cancelLabel,
  selectionMode,
  selectedKey,
  onSelectionChange,
}: ActionSheetSurfaceProps) {
  const styles = actionSheet({ platform });

  const disabledKeys = options.filter((option) => option.isDisabled).map((option) => option.id);

  const menuSelectionProps =
    selectionMode === "single"
      ? {
          selectionMode: "single" as const,
          selectedKeys: selectedKey ? new Set([selectedKey]) : new Set<string>(),
          onSelectionChange: (keys: Selection) => {
            const key = firstSelectedKey(keys);
            if (key !== undefined) onSelectionChange?.(key);
            onOpenChange(false);
          },
        }
      : {
          onAction: (key: Key) => {
            options.find((option) => option.id === key)?.onAction?.();
            onOpenChange(false);
          },
        };

  const menu = (
    <Menu aria-label={title ?? label} className={styles.menu()} disabledKeys={disabledKeys} {...menuSelectionProps}>
      {options.map((option) => (
        <MenuItem
          key={option.id}
          className={styles.item({ class: option.destructive ? "text-pink-500" : undefined })}
          href={option.href}
          id={option.id}
          isDisabled={option.isDisabled}
          textValue={option.label}
        >
          {({ isSelected }) => (
            <>
              {/* Material 3 (left-aligned) shows leading icons; iOS stays centered + icon-less per the design. */}
              {platform === "android" && option.icon}
              <span>{option.label}</span>
              {isSelected && <Icon className={styles.check()} name="check" size="20" />}
            </>
          )}
        </MenuItem>
      ))}
    </Menu>
  );

  return (
    <MobileSheet
      className={styles.panel()}
      label={label}
      onOpenChange={onOpenChange}
      presentation={platform === "ios" ? "floating" : "docked"}
      isOpen={isOpen}
    >
      <div className={styles.dialog()}>
        {platform === "ios" ? (
          <>
            <div className={styles.group()}>
              {title && <div className={styles.title()}>{title}</div>}
              {menu}
            </div>
            <Button className={styles.cancel()} onPress={() => onOpenChange(false)}>
              {cancelLabel}
            </Button>
          </>
        ) : (
          <>
            {title && <div className={styles.title()}>{title}</div>}
            {menu}
          </>
        )}
      </div>
    </MobileSheet>
  );
}
